import * as THREE from 'three';
import { ColoredObjectSchema } from './ColoredObject';
import { GlintEffect } from './glint';
import { SelectableObject } from './selectableObject';
import { stringSchema } from '@/data/common';
import z from 'zod';

export const CylinderSchema = ColoredObjectSchema.extend({
  name: stringSchema.default('Cylinder'),
  radius: z.number().default(0.5),
  height: z.number().default(1),
});

export class Cylinder extends SelectableObject<typeof CylinderSchema> {
  static override schema = CylinderSchema;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly glintEffect: GlintEffect;

  constructor(options: z.input<typeof CylinderSchema> = {}) {
    super(options);

    this.material = new THREE.MeshStandardMaterial({
      color: this.data.color,
      emissive: 0x000000,
      roughness: 0.35,
      metalness: 0.05,
    });

    const geometry = new THREE.CylinderGeometry(
      this.data.radius,
      this.data.radius,
      this.data.height,
      32,
    );

    this.mesh = new THREE.Mesh(geometry, this.material);

    this.glintEffect = new GlintEffect(this.material);
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override getPickTargets() {
    return [this.mesh];
  }

  override getAabb() {
    return new THREE.Box3().setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(
        this.data.radius * 2,
        this.data.height,
        this.data.radius * 2,
      ),
    );
  }

  override update(delta: number): void {
    this.glintEffect.update(delta);
  }

  override dispose() {
    super.dispose();
    this.glintEffect.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  protected override onSelectionChanged(selected: boolean): void {
    this.glintEffect.setSelected(selected);
  }
}
